import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { LoginType } from 'types'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { UserUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import {
  confirmEndUserDeclaration,
  getEndUserDeclaration,
} from 'core/userDeclaration/api/userDeclarationService'
import { GetAccountResponse } from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import { ApiGetUserDeclarationResponse, UserDeclarationState } from './types'

const initialState: UserDeclarationState = {
  userDeclarationData: {},
  confirmUserDeclarationRequest: {},
  userDeclarationPopupIsShown: false,
  privacyPolicyPopupIsShown: false,
}

const userDeclarationSlice = createSlice({
  name: 'userDeclaration',
  initialState,
  reducers: {
    getUserDeclarationRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiGetUserDeclarationResponse>>
    ) {
      state.userDeclarationData = action.payload
    },
    changeUserDeclarationPopupVisibility(state, action: PayloadAction<boolean>) {
      state.userDeclarationPopupIsShown = action.payload
    },
    changePrivacyPolicyPopupVisibility(state, action: PayloadAction<boolean>) {
      state.privacyPolicyPopupIsShown = action.payload
    },
    confirmUserDeclarationRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.confirmUserDeclarationRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

export const { changePrivacyPolicyPopupVisibility, changeUserDeclarationPopupVisibility } =
  userDeclarationSlice.actions

const { confirmUserDeclarationRequested, getUserDeclarationRequested } =
  userDeclarationSlice.actions

export default userDeclarationSlice.reducer

const handleGetUserDeclaration =
  (response: ApiMiddlewarePayload<ApiGetUserDeclarationResponse>): AppThunk =>
  async dispatch => {
    response.result && dispatch(changeUserDeclarationPopupVisibility(true))
    dispatch(getUserDeclarationRequested(response))
  }

export const getUserDeclaration = () => {
  return createApiAction({
    apiCalls: {
      data: getEndUserDeclaration,
    },
    payloadAction: getUserDeclarationRequested,
    nextAction: handleGetUserDeclaration,
  })
}

const handleGetPrivacyPolicy =
  (response: ApiMiddlewarePayload<ApiGetUserDeclarationResponse>): AppThunk =>
  async dispatch => {
    !response.result && dispatch(changePrivacyPolicyPopupVisibility(false))
    dispatch(getUserDeclarationRequested(response))
  }

export const getPrivacyPolicy = () => {
  return createApiAction({
    apiCalls: {
      data: getEndUserDeclaration,
    },
    payloadAction: getUserDeclarationRequested,
    nextAction: handleGetPrivacyPolicy,
  })
}

const handleConfirmUserDeclaration =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    const { error, result } = response
    if (!error && result) {
      const userAccountInfo = UserUtils.getUserAccountInfo()
      const newUserAccountInfo = {
        ...userAccountInfo,
        isDeclarationConfirmed: true,
      }

      dispatch(changeUserDeclarationPopupVisibility(false))

      const { loginType } = UserUtils.getUserLoginParams()
      UserUtils.storeUserAccountInfo(newUserAccountInfo as GetAccountResponse)

      let oceanEnabled
      let useLegacyClient

      switch (loginType) {
        case LoginType.Proxy: {
          const { isOceanEnabled, shouldUseLegacyClient } =
            getState().proxyRedirect.authenticationFromProxy.result?.proxyAuthentication || {}
          oceanEnabled = isOceanEnabled
          useLegacyClient = shouldUseLegacyClient
          break
        }
        case LoginType.Sso: {
          const { isOceanEnabled, shouldUseLegacyClient } =
            getState().sso.authenticationWithSso.result?.ssoAuthentication || {}
          oceanEnabled = isOceanEnabled
          useLegacyClient = shouldUseLegacyClient
          break
        }
        default: {
          const { isOceanEnabled, shouldUseLegacyClient } = getState().auth
          oceanEnabled = isOceanEnabled
          useLegacyClient = shouldUseLegacyClient
          break
        }
      }

      if (!oceanEnabled) {
        dispatch(redirectToSta())
      } else if (useLegacyClient) {
        dispatch(redirectToSta({ pluginName: 'TaskStatusPlugin', moduleName: null }))
      } else {
        dispatch(push(homeRoutes.root))
      }
    }
    dispatch(confirmUserDeclarationRequested(response))
  }

export const confirmUserDeclaration = () => {
  return createApiAction({
    apiCalls: {
      account: confirmEndUserDeclaration,
    },
    payloadAction: confirmUserDeclarationRequested,
    nextAction: handleConfirmUserDeclaration,
    enableErrorNotification: true,
  })
}
